/**
 * LiteGuideAssessment — one question at a time, smooth fade transitions,
 * anti-repetitive AI phrasing, AI voice speaks all AI lines.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  createHelpSession,
  sendHelpMessage,
  updateAssessmentStatus,
  updateHelpSessionContext,
} from "../api/client";
import { useTheme } from "../context/ThemeContext";
import { partAnswerKey } from "../utils/assessmentKeys";
import { buildHelpContext, buildHelpPillar } from "../utils/helpContext";
import { isQuestionAnswered } from "../utils/questionProgress";
import { useTTS, useSTT, matchVoiceToOption, typingDelayWithVoice } from "../hooks/useSpeech";
import ChatInputBar from "./chat/ChatInputBar";
import AssessmentSharedNav from "./AssessmentSharedNav";
import {
  AssessmentPillarStatus,
  AssessmentQuestionNav,
  AssessmentRatingGuide,
} from "./AssessmentChromeBars";
import { assessmentChromeTokens } from "./assessmentChromeTokens";
import { assessmentIntroMeta } from "../utils/assessmentIntroMeta";
import { productName, scaleOptions, isFiveXType } from "../utils/participantAssessmentType";
import { ASSESSMENT_UX_GUIDE } from "../utils/assessmentUxMode";

/* ══════════════════════════════════════════════════════════════════
   PHRASE ARRAYS  — 5-6+ variants each, never the same twice in a row
   ══════════════════════════════════════════════════════════════════ */

function buildWelcomeLines({ questionCount, pillarCount, isFull, assessmentType }) {
  const q = Math.max(0, Number(questionCount) || 0);
  const pillars = Math.max(0, Number(pillarCount) || 0);
  const fiveX = isFiveXType(assessmentType);
  const pname = productName(assessmentType);
  const scope =
    pillars > 0
      ? `${pillars} section${pillars === 1 ? "" : "s"}`
      : fiveX
        ? "the key dimensions of organizational resilience"
        : "the key areas of this assessment";
  const qPhrase =
    q > 0
      ? `${q} question${q === 1 ? "" : "s"}`
      : isFull
        ? "the full assessment"
        : "a short set of questions";

  if (isFull) {
    return [
      `Welcome to the full ${pname}.`,
      `We'll work through ${scope} — one question at a time.`,
      "For each statement, share your rating — same style as the lite assessment.",
      `Take your time across ${qPhrase}. There are no right or wrong answers.`,
      "Ready? Let's begin.",
    ];
  }

  return [
    `Hey — I'm your ${pname} guide.`,
    `We'll work through ${scope}. One question at a time.`,
    fiveX
      ? "For each statement, rate two things: how important it is, and how well you're actually delivering on it today."
      : "For each statement, give your honest rating based on what you see today.",
    `There are no right or wrong answers — just your honest read across ${qPhrase}.`,
    "Ready? Let's go.",
  ];
}

const makeResumeLines = (answered, total) => {
  const done = Math.max(0, Number(answered) || 0);
  const all = Math.max(done, Number(total) || 0);
  const left = Math.max(0, all - done);
  if (all > 0 && left > 0) {
    return [
      `Welcome back. ${done} answered, ${left} to go. Picking up from where you left off.`,
    ];
  }
  return [
    `Welcome back. You've answered ${done} question${done === 1 ? "" : "s"} so far. Picking up where you left off.`,
  ];
};

const makeFullResumeLines = (n) => [
  `Welcome back. You've answered ${n} questions so far. Picking up where you left off.`,
];

const makeFullPillarIntroLines = (title, stepNum, totalSteps) => [
  `Now exploring ${title || "this pillar"}.`,
  `Pillar ${stepNum} of ${totalSteps} — one question at a time.`,
  `Let's go.`,
];

const IMPORTANCE_ASKS = [
  "How important is this to your organization's success?",
  "Rate how critical this is to what you're building right now.",
  "How much does this matter to your company at this stage?",
  "Where does this land on the importance scale for you?",
  "How central is this to how your organization performs?",
  "Does this belong at the top of your priority list?",
];

const IMP_ACKS_HIGH = [
  "Got it — this clearly matters.",
  "Noted. High priority.",
  "Clear. That's a strong signal.",
  "Right. So it's important.",
  "Understood — this is significant.",
];
const IMP_ACKS_MED  = ["Got it.", "Noted.", "Understood.", "Clear.", "Fair."];
const IMP_ACKS_LOW  = ["Fair enough.", "Got it.", "Noted.", "Understood.", "Makes sense."];

const AGR_ASKS_CRITICAL = [
  "Now the harder question — how well is your organization actually delivering on this today?",
  "High bar. How close are you to clearing it right now?",
  "And in practice — how well is this actually happening?",
  "Be honest: where does current performance sit on this?",
  "So how well is your organization executing against this standard today?",
  "The real question: how much of this is actually being done?",
];
const AGR_ASKS_IMPORTANT = [
  "Now — how well is your organization currently performing on this?",
  "And current execution — where does it stand?",
  "How would you rate performance on this in practice today?",
  "Where does your organization sit on this right now?",
  "And how well is this actually being delivered?",
];
const AGR_ASKS_LOW = [
  "For completeness — how well is it being executed anyway?",
  "And where does current performance land on this?",
  "Even so — how would you rate execution on this today?",
  "How well is this playing out in practice?",
];

const OBS_MAJOR_GAP = [
  "That gap matters — high importance, but execution isn't there yet.",
  "Worth noting: what matters most here isn't being fully delivered.",
  "Real tension there. High priority, but performance isn't keeping up.",
  "That's a gap that deserves attention.",
  "Critical blind spot — prioritized, but underperforming.",
];
const OBS_MODERATE_GAP = [
  "Some daylight there — importance outpacing execution.",
  "A gap worth watching.",
  "Room to improve, but not a crisis.",
  "Partially there — not fully bridged yet.",
  "Noted. Not quite where it needs to be.",
];
const OBS_ALIGNED = [
  "Solid — importance and execution are well matched here.",
  "Good signal there.",
  "Strong on both fronts.",
  "That's a healthy read.",
  "Well calibrated.",
];
const OBS_LOW_IMP = [
  "Got it.",
  "Noted.",
  "Understood.",
  "Makes sense.",
  "Fair enough.",
];
const OBS_UNABLE = [
  "Noted — outside your current visibility.",
  "Understood — not enough signal to assess.",
  "Got it. Moving right along.",
  "Marked for limited visibility. That's fine.",
  "Noted — flagged as outside your area.",
];

/* ══════════════════════════════════════════════════════════════════
   INLINE MARKDOWN RENDERER — handles **bold**, *italic*, newlines
   ══════════════════════════════════════════════════════════════════ */
function MdText({ children, style }) {
  if (!children) return null;
  // Split on **bold** and *italic* markers, preserving the tokens
  const parts = String(children).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  const nodes = parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*"))
      return <em key={i}>{part.slice(1, -1)}</em>;
    // Render newlines as breaks
    return part.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>{line}{j < arr.length - 1 && <br />}</span>
    ));
  });
  return <span style={style}>{nodes}</span>;
}

/* Transition phrases shown briefly before the screen clears to the next question */
const TRANSITIONS = [
  "Let's pivot to the next one.",
  "Moving right along.",
  "Next up.",
  "Got it — let's keep going.",
  "On to the next.",
  "Good. Let's continue.",
  "Alright. Moving on.",
];

const TEXT_ASKS = [
  "Take a moment — what's your response to this?",
  "Share your thoughts below. A sentence or two is enough.",
  "Type your answer when you're ready.",
  "What would you say here? Enter your response below.",
];

const DROPDOWN_ASKS = [
  "Which option best fits? Pick one below.",
  "Select the option that applies here.",
  "Choose the best match from the options below.",
];

const FREE_ACKS = [
  "Got it — thanks.",
  "Noted.",
  "Understood.",
  "Clear — moving on.",
  "Thanks, that's helpful.",
];

const PILLAR_INTROS = {
  "Strategic Clarity":     "how clearly your leadership aligns on direction and top priorities",
  "Scalable Talent":       "attracting, developing, and retaining the people needed to grow",
  "Relentless Focus":      "eliminating distractions and consistently acting on what matters most",
  "Disciplined Execution": "following through on commitments and turning plans into results",
  "Energized Culture":     "engagement, energy, and safety that drive your team to perform",
  "Financial Performance": "financial health and performance relative to growth goals",
};

/* ══════════════════════════════════════════════════════════════════
   PURE HELPERS
   ══════════════════════════════════════════════════════════════════ */

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Pick a random element, avoiding repeating the previous pick for the same key. */
const _lastPickMap = {};
function pick(arr, key = "_") {
  if (!arr || arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  let idx;
  const last = _lastPickMap[key] ?? -1;
  do { idx = Math.floor(Math.random() * arr.length); } while (idx === last);
  _lastPickMap[key] = idx;
  return arr[idx];
}

function getObsArray(imp, agr) {
  if (agr === "Unable to assess") return OBS_UNABLE;
  const i = { "Not Important": 0, "Important": 1, "Very Important": 2, "Critical": 3 }[imp] ?? 0;
  const a = { "Not at all": 0, "To a small extent": 1, "To a moderate extent": 2, "To a great extent": 3, "Completely": 4 }[agr] ?? 0;
  if (i <= 1) return OBS_LOW_IMP;
  const gap = i - a;
  if (gap >= 2) return OBS_MAJOR_GAP;
  if (gap === 1) return OBS_MODERATE_GAP;
  return OBS_ALIGNED;
}

function getOptions(part, ptype, assessmentType) {
  if (part?.options?.length) return part.options;
  const fromType = assessmentType ? scaleOptions(assessmentType, ptype) : [];
  if (fromType.length) return fromType;
  return ptype === "importance"
    ? ["Not Important", "Important", "Very Important", "Critical"]
    : ["Not at all", "To a small extent", "To a moderate extent", "To a great extent", "Completely", "Unable to assess"];
}

function findStepForQuestion(steps, questionId) {
  const target = String(questionId ?? "");
  for (const s of steps || [])
    for (const q of s.questions || [])
      if (String(q.id) === target) return s;
  return null;
}

function getQuestionMode(question) {
  const qtype = (question?.type || "mcq").toLowerCase();
  const partType = (question?.parts?.[0]?.type || "").toLowerCase();
  if (qtype === "rating" || partType === "rating") return "rating";
  if (qtype === "text") return "text";
  if (qtype === "dropdown" || qtype === "multi") return "dropdown";
  return "mcq";
}

function getRatingPart(question) {
  const parts = question?.parts || [];
  return parts.find((p) => (p.type || "").toLowerCase() === "rating") || parts[0] || { type: "rating", max_stars: 5 };
}

function getDropdownPart(question) {
  const parts = question?.parts || [];
  return parts.find((p) => p.type === "importance" && p.options?.length)
    || parts.find((p) => p.options?.length)
    || parts[0];
}

function getTextPart(question) {
  return question?.parts?.[0] || { type: "text" };
}

const CONFETTI_COLORS = ["#004f73","#1a7fa8","#38bdf8","#67e8f9","#0ea5e9","#7dd3fc","#bae6fd","#ffffff"];

/* ══════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
   ══════════════════════════════════════════════════════════════════ */

function AiDot({ size = 32, dark, pulse = false }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: dark
        ? "linear-gradient(135deg,rgba(0,79,115,.75),rgba(26,127,168,.5))"
        : "linear-gradient(135deg,#004f73,#1a7fa8)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: dark ? "0 0 14px rgba(56,189,248,0.18)" : "0 2px 10px rgba(0,79,115,0.22)",
      animation: pulse ? "lga-orb-pulse 2.2s ease-in-out infinite" : "none",
    }}>
      <svg width={size * 0.42} height={size * 0.42} viewBox="0 0 24 24" fill="#fff">
        <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" />
      </svg>
    </div>
  );
}

function Cursor() {
  return (
    <span style={{
      display: "inline-block", width: 2, height: "0.9em",
      background: "currentColor", marginLeft: 2,
      verticalAlign: "text-bottom",
      animation: "lga-cursor 0.85s step-end infinite",
    }} />
  );
}

function ThreeDots({ dark }) {
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: "50%",
          background: dark ? "rgba(56,189,248,0.6)" : "#004f73",
          animation: `lga-bounce 1.2s ${i * 0.18}s ease-in-out infinite`,
        }} />
      ))}
    </span>
  );
}

function Confetti({ active }) {
  const p = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    id: i, left: 15 + Math.random() * 70, xDrift: (Math.random() - 0.5) * 240,
    rise: 90 + Math.random() * 110, rotate: -200 + Math.random() * 400,
    size: 4 + Math.random() * 5, aspect: Math.random() > 0.4 ? 2.2 : 1,
    round: Math.random() > 0.5, color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: Math.random() * 0.5, dur: 1.1 + Math.random() * 0.9,
  })), []);
  if (!active) return null;
  return (
    <div style={{ position: "fixed", bottom: 80, left: 0, right: 0, height: 220, pointerEvents: "none", zIndex: 60, overflow: "hidden" }}>
      {p.map((x) => (
        <div key={x.id} style={{
          position: "absolute", left: `${x.left}%`, bottom: 0,
          width: x.size, height: x.size * x.aspect,
          background: x.color, borderRadius: x.round ? "50%" : 2, opacity: 0,
          animation: `lga-confetti ${x.dur}s ${x.delay}s ease-out forwards`,
          "--x": `${x.xDrift}px`, "--y": `-${x.rise}px`, "--r": `${x.rotate}deg`,
        }} />
      ))}
    </div>
  );
}

/* Landing ---------------------------------------------------------------- */
function LandingScreen({
  companyName,
  companyLogo,
  onBegin,
  dark,
  assessmentTrack = "lite",
  trackBanner,
  questionCount = null,
  assessmentType,
}) {
  const isFull = assessmentTrack === "full";
  const fallbackCount = isFull ? 65 : 10;
  const meta = assessmentIntroMeta({
    track: isFull ? "full" : "lite",
    questionCount: questionCount ?? fallbackCount,
  });
  const description = isFull
    ? isFiveXType(assessmentType)
      ? "A guided conversation across all six pillars of organizational resilience — importance, agreement, and open responses. One question at a time."
      : "A guided conversation across every section of this assessment. One question at a time."
    : isFiveXType(assessmentType)
      ? "A guided conversation across selected dimensions of organizational resilience. Rate importance and agreement for each statement — one question at a time."
      : "A guided conversation across selected areas of this assessment. One question at a time.";

  const boxBorder = dark ? "rgba(255,255,255,0.12)" : "rgba(0,79,115,0.14)";
  const tagBg = dark ? "rgba(0,79,115,0.28)" : "rgba(0,79,115,0.08)";
  const tagBorder = dark ? "rgba(56,189,248,0.35)" : "rgba(0,79,115,0.22)";
  const tagColor = dark ? "rgba(103,232,249,0.92)" : "#004f73";

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: "32px 24px", textAlign: "center", animation: "lga-fadein 0.5s ease both" }}>
      <div style={{
        width: "100%", maxWidth: 440,
        padding: "28px 26px 22px", borderRadius: 18,
        border: `1px solid ${boxBorder}`,
      }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
          <AiDot size={72} dark={dark} pulse />
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase",
            color: tagColor, padding: "4px 12px", borderRadius: 999,
            background: tagBg, border: `1px solid ${tagBorder}`,
          }}>
            {meta.label}
          </span>
        </div>

        <h1 style={{ fontSize: 24, fontWeight: 800, color: dark ? "#fff" : "#0f172a", margin: "0 0 8px", letterSpacing: "-0.02em" }}>
          {productName(assessmentType)}
        </h1>

        {companyName && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 10 }}>
            {companyLogo && <img src={companyLogo} alt="" style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }} />}
            <span style={{ fontSize: 13, fontWeight: 600, color: dark ? "rgba(255,255,255,0.45)" : "#64748b" }}>{companyName}</span>
          </div>
        )}

        {trackBanner && (
          <p style={{ fontSize: 12, fontWeight: 600, color: dark ? "rgba(56,189,248,0.75)" : "#004f73", margin: "0 0 12px" }}>
            {trackBanner}
          </p>
        )}

        <p style={{ fontSize: 14, lineHeight: 1.75, color: dark ? "rgba(255,255,255,0.55)" : "#475569", margin: "0 0 16px" }}>
          {description}
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 22 }}>
          {[
            { text: `${meta.questions} questions` },
            { text: `~${meta.minutes} min` },
          ].map(({ text }) => (
            <span key={text} style={{
              fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 999,
              color: dark ? "rgba(255,255,255,0.62)" : "#004f73",
              background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,79,115,0.06)",
              border: dark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,79,115,0.12)",
            }}>
              {text}
            </span>
          ))}
        </div>

        <button
          type="button" onClick={onBegin}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.03)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
            width: "100%", padding: "13px 30px", borderRadius: 999, fontSize: 14, fontWeight: 700,
            background: "linear-gradient(105deg,#004f73 0%,#105a7c 45%,#1a7fa8 100%)",
            backgroundSize: "200% auto", animation: "lga-shimmer 3s linear infinite",
            color: "#fff", border: "none", cursor: "pointer",
            boxShadow: "0 0 28px rgba(0,79,115,0.42)", transition: "transform 0.15s",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          Begin Assessment
        </button>
      </div>
    </div>
  );
}

/* Done ------------------------------------------------------------------- */
function DoneScreen({ onComplete, saving, dark, assessmentTrack = "lite" }) {
  const isFull = assessmentTrack === "full";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: "32px 24px", textAlign: "center", animation: "lga-fadein 0.5s ease both" }}>
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: dark ? "rgba(74,222,128,0.15)" : "#f0fdf4",
        border: dark ? "1px solid rgba(74,222,128,0.35)" : "1px solid #bbf7d0",
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={dark ? "#4ade80" : "#16a34a"} strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: dark ? "#fff" : "#0f172a", margin: "0 0 10px", letterSpacing: "-0.02em" }}>
        Assessment complete.
      </h2>
      <p style={{ fontSize: 14, lineHeight: 1.75, color: dark ? "rgba(255,255,255,0.50)" : "#475569", maxWidth: 360, margin: "0 auto 28px" }}>
        {isFull
          ? "You've completed the full assessment across all six pillars. Thank you."
          : "You've worked through all six pillars. The results are ready whenever you are."}
      </p>
      <button
        type="button" onClick={onComplete} disabled={saving}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "12px 28px", borderRadius: 999, fontSize: 14, fontWeight: 700,
          background: "linear-gradient(105deg,#004f73 0%,#1a7fa8 100%)",
          backgroundSize: "200% auto", animation: "lga-shimmer 3s linear infinite",
          color: "#fff", border: "none", cursor: saving ? "not-allowed" : "pointer",
          opacity: saving ? 0.55 : 1, boxShadow: "0 0 20px rgba(0,79,115,0.38)",
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff">
          <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" />
        </svg>
        {saving ? "Saving…" : isFull ? "Submit Assessment" : "Complete Lite Assessment"}
      </button>
    </div>
  );
}

/* Inline rich statement — highlights term phrases within question text ---- */
function RichStatement({ text, terms, dark, style }) {
  if (!terms || !terms.length) return <span style={style}>{text}</span>;

  // Build a single regex that matches any term phrase (case-insensitive)
  const escaped = terms.map(t => t.phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  let segments;
  try {
    const re = new RegExp(`(${escaped.join("|")})`, "gi");
    segments = text.split(re);
  } catch {
    return <span style={style}>{text}</span>;
  }

  return (
    <span style={style}>
      {segments.map((seg, i) => {
        const term = terms.find(t => t.phrase.toLowerCase() === seg.toLowerCase());
        if (!term) return <span key={i}>{seg}</span>;
        return <InlineTermTooltip key={i} phrase={seg} definition={term.definition} dark={dark} />;
      })}
    </span>
  );
}

/* Inline term highlight with hover tooltip ------------------------------- */
function InlineTermTooltip({ phrase, definition, dark }) {
  const [open, setOpen] = useState(false);
  const borderColor = dark ? "rgba(103,232,249,0.70)" : "rgba(0,79,115,0.50)";
  return (
    <span style={{ position: "relative", display: "inline" }}>
      <button
        type="button"
        className="lga-term-trigger"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onBlur={() => setOpen(false)}
        style={{
          borderBottom: `1.5px dotted ${borderColor}`,
          color: open ? (dark ? "#67e8f9" : "#004f73") : "inherit",
          transition: "color 0.15s",
        }}
      >
        {phrase}
      </button>
      {open && (
        <span
          className="lga-term-panel"
          style={{
          position: "absolute", top: "calc(100% + 8px)", left: "50%",
          transform: "translateX(-50%)",
          zIndex: 200, minWidth: 200,
          padding: "9px 13px", borderRadius: 10,
          fontSize: 12, lineHeight: 1.6, fontWeight: 400,
          color: dark ? "rgba(255,255,255,0.82)" : "#334155",
          background: dark ? "rgba(8,18,32,0.97)" : "#fff",
          border: dark ? "1px solid rgba(56,189,248,0.22)" : "1px solid rgba(0,79,115,0.16)",
          boxShadow: dark ? "0 8px 28px rgba(0,0,0,0.60)" : "0 6px 24px rgba(0,79,115,0.13)",
          animation: "lga-fadein 0.13s ease both",
          whiteSpace: "normal",
        }}
        >
          <span style={{
            display: "block", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", color: dark ? "#38bdf8" : "#004f73", marginBottom: 4,
          }}>
            {phrase}
          </span>
          {definition}
        </span>
      )}
    </span>
  );
}

/* Term tooltip badge ----------------------------------------------------- */
function TermTooltip({ phrase, definition, dark }) {
  const [hover, setHover] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "3px 10px 3px 8px", borderRadius: 999,
          fontSize: 12, fontWeight: 600, cursor: "default",
          background: hover
            ? dark ? "rgba(56,189,248,0.16)" : "rgba(0,79,115,0.11)"
            : dark ? "rgba(56,189,248,0.08)" : "rgba(0,79,115,0.06)",
          border: dark ? "1px solid rgba(56,189,248,0.22)" : "1px solid rgba(0,79,115,0.18)",
          color: dark ? "#67e8f9" : "#004f73",
          transition: "background 0.15s",
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 16v-4M12 8h.01" />
        </svg>
        {phrase}
      </button>
      {hover && (
        <span style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: 0,
          zIndex: 100, minWidth: 220, maxWidth: 380,
          padding: "10px 14px", borderRadius: 12,
          fontSize: 13, lineHeight: 1.65,
          color: dark ? "rgba(255,255,255,0.80)" : "#334155",
          background: dark ? "rgba(10,20,35,0.96)" : "#fff",
          border: dark ? "1px solid rgba(56,189,248,0.20)" : "1px solid rgba(0,79,115,0.14)",
          boxShadow: dark ? "0 8px 28px rgba(0,0,0,0.55)" : "0 6px 24px rgba(0,79,115,0.12)",
          animation: "lga-fadein 0.15s ease both",
          pointerEvents: "none", whiteSpace: "normal",
        }}>
          {definition}
        </span>
      )}
    </span>
  );
}

/* Rating row ------------------------------------------------------------- */
function RatingRow({ options, selected, locked, onSelect, dark, compact }) {
  return (
    <div style={{
      display: "flex",
      flexWrap: compact ? "nowrap" : "wrap",
      gap: compact ? 6 : 8,
      overflowX: compact ? "auto" : "visible",
      paddingBottom: compact ? 2 : 0,
      animation: "lga-ratingin 0.3s ease both",
    }}>
      {options.map((opt) => {
        const isSel = selected === opt;
        const isOther = locked && !isSel;
        return (
          <button
            key={opt} type="button"
            disabled={locked}
            onClick={() => !locked && onSelect(opt)}
            style={{
              display: "inline-flex", alignItems: "center", gap: compact ? 4 : 6, flexShrink: 0,
              padding: compact ? "7px 13px" : "9px 18px",
              borderRadius: 999,
              fontSize: compact ? 12 : 13,
              fontWeight: isSel ? 700 : 500,
              whiteSpace: "nowrap",
              cursor: locked ? "default" : "pointer",
              transition: "all 0.18s", userSelect: "none",
              opacity: isOther ? 0.35 : 1,
              background: isSel ? "#004f73" : dark ? "rgba(255,255,255,0.06)" : "#fff",
              border: isSel
                ? dark ? "2px solid #38bdf8" : "2px solid #004f73"
                : dark ? "1.5px solid rgba(255,255,255,0.13)" : "1.5px solid rgba(0,79,115,0.20)",
              color: isSel ? "#fff" : dark ? "rgba(255,255,255,0.78)" : "#334155",
              boxShadow: isSel
                ? dark ? "0 0 0 3px rgba(56,189,248,0.20)" : "0 0 0 3px rgba(0,79,115,0.14)"
                : "none",
            }}
          >
            {isSel && (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} style={{ flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */
export default function LiteGuideAssessment({
  surveyToken, steps, companyName, companyLogo,
  answers, setAnswerField, onComplete, saving,
  /* Full-assessment props */
  assessmentTrack = "lite",
  questions: questionsProp = null,
  currentStep = 1,
  totalSteps,
  onStepComplete,
  skipLanding = false,
  trackBanner = null,
  /** Override landing/intro chips (e.g. remaining full questions after LITE). */
  landingQuestionCount = null,
  liteQuestionIds = null,
  uxMode = ASSESSMENT_UX_GUIDE,
  onUxModeChange = null,
  assessmentType,
  productLogoUrl = null,
  productDisplayName = null,
  primaryColor = null,
}) {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === "dark";
  const isFullMode = assessmentTrack === "full";

  /* ── question lists ─────────────────────────────────────────────── */
  const flowQuestions = useMemo(() => {
    if (questionsProp?.length) return questionsProp;
    return (steps || []).flatMap((s) => s.questions || []);
  }, [questionsProp, steps]);

  const allAssessmentQuestions = useMemo(
    () => (steps || []).flatMap((s) => s.questions || []),
    [steps]
  );

  const progressQuestions = isFullMode ? allAssessmentQuestions : flowQuestions;
  const introQuestionCount = useMemo(() => {
    if (landingQuestionCount != null && Number(landingQuestionCount) >= 0) {
      return Number(landingQuestionCount);
    }
    if (isFullMode && Array.isArray(liteQuestionIds) && liteQuestionIds.length) {
      const exclude = new Set(liteQuestionIds);
      return allAssessmentQuestions.filter((q) => q?.id && !exclude.has(q.id)).length;
    }
    return flowQuestions.length;
  }, [
    landingQuestionCount,
    isFullMode,
    liteQuestionIds,
    allAssessmentQuestions,
    flowQuestions.length,
  ]);
  const introQuestionCountRef = useRef(introQuestionCount);
  useEffect(() => {
    introQuestionCountRef.current = introQuestionCount;
  }, [introQuestionCount]);

  const helpPillars = useMemo(() => {
    const sourceSteps = isFullMode
      ? (steps || []).filter((s) => s.step <= currentStep)
      : (steps || []);
    return sourceSteps.map((s) => {
      const qs = isFullMode && s.step === currentStep
        ? flowQuestions
        : (s.questions || []);
      return buildHelpPillar({ step: s.step, title: s.title, questions: qs });
    }).filter((p) => p.questions.length);
  }, [steps, isFullMode, currentStep, flowQuestions]);

  /* ── progress ───────────────────────────────────────────────────── */
  const answeredCount = useMemo(
    () => progressQuestions.filter((q) => isQuestionAnswered(q, answers)).length,
    [progressQuestions, answers]
  );
  const scopeAnsweredCount = useMemo(
    () => flowQuestions.filter((q) => isQuestionAnswered(q, answers)).length,
    [flowQuestions, answers]
  );
  const allScopeAnswered = flowQuestions.length > 0 && flowQuestions.length === scopeAnsweredCount;
  const allAssessmentAnswered = allAssessmentQuestions.length > 0
    && allAssessmentQuestions.length === answeredCount;
  const progressPct = progressQuestions.length
    ? Math.round((answeredCount / progressQuestions.length) * 100)
    : 0;

  /* ── phase ──────────────────────────────────────────────────────── */
  const [phase, setPhase] = useState(() => {
    if (scopeAnsweredCount > 0) return "question";
    if (!skipLanding && answeredCount > 0) return "question";
    return skipLanding ? "welcome" : "landing";
  });

  /* ── screen visibility — drives CSS fade-in/out ─────────────────── */
  const [visible, setVisible] = useState(false);

  /* ── question display state ─────────────────────────────────────── */
  const [pillarTitle, setPillarTitle]       = useState("");
  const [pillarIntro, setPillarIntro]       = useState("");
  const [questionNum, setQuestionNum]       = useState(1);
  const [questionId, setQuestionId]         = useState("");
  const [statementText, setStatementText]   = useState("");
  const [contextTerms, setContextTerms]     = useState([]); // [{phrase, definition}]
  const [aiLine, setAiLine]                 = useState("");
  const [aiLineVisible, setAiLineVisible]   = useState(false);
  const [typingStatement, setTypingStatement] = useState(false);
  const [typingAiLine, setTypingAiLine]     = useState(false);

  /* ── rating state ───────────────────────────────────────────────── */
  const [showImp, setShowImp]         = useState(false);
  const [impOptions, setImpOptions]   = useState([]);
  const [impSelected, setImpSelected] = useState("");
  const [impLocked, setImpLocked]     = useState(false);
  const [showAgr, setShowAgr]         = useState(false);
  const [agrOptions, setAgrOptions]   = useState([]);
  const [agrSelected, setAgrSelected] = useState("");
  const [agrLocked, setAgrLocked]     = useState(false);

  /* ── text / dropdown / star-rating question state ───────────────── */
  const [showTextInput, setShowTextInput]       = useState(false);
  const [textValue, setTextValue]               = useState("");
  const [textLocked, setTextLocked]             = useState(false);
  const [showDropdown, setShowDropdown]         = useState(false);
  const [dropdownOptions, setDropdownOptions]   = useState([]);
  const [dropdownSelected, setDropdownSelected] = useState("");
  const [dropdownLocked, setDropdownLocked]     = useState(false);
  const [showStars, setShowStars]               = useState(false);
  const [starMax, setStarMax]                   = useState(5);
  const [starSelected, setStarSelected]         = useState(0);
  const [starLocked, setStarLocked]             = useState(false);

  /* ── welcome state ──────────────────────────────────────────────── */
  const [welcomeText, setWelcomeText]     = useState("");
  const [welcomeIdx, setWelcomeIdx]       = useState(0);
  const [welcomeTyping, setWelcomeTyping] = useState(false);
  const totalWelcomeLines                 = useRef(5);

  /* ── help — inline conversation exchanges ────────────────────────── */
  /* Each entry: { q: string, a: string, loading: bool } */
  const [helpExchanges, setHelpExchanges] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  /* Scroll anchor at end of conversation */
  const convoEndRef = useRef(null);

  /* ── confetti ───────────────────────────────────────────────────── */
  const [confetti, setConfetti] = useState(false);

  /* ── voice ──────────────────────────────────────────────────────── */
  const { enabled: voiceEnabled, setEnabled: setVoiceEnabled, speakAsync, stop: stopSpeak, waitForIdle, supported: ttsSupported } = useTTS();
  const { listening: micListening, start: startSTT, stop: stopSTT, supported: sttSupported } = useSTT();

  /* ── stable refs ────────────────────────────────────────────────── */
  const cancelRef     = useRef(false);
  const runIdRef      = useRef(0);
  const impLockedRef  = useRef(false);
  const agrLockedRef  = useRef(false);
  const textLockedRef = useRef(false);
  const dropdownLockedRef = useRef(false);
  const starLockedRef = useRef(false);
  const sessionIdRef  = useRef(null);
  const curDataRef    = useRef(null);   // { question, impPart, agrPart }
  const impValueRef   = useRef("");
  const currentIdxRef = useRef(0);
  const activeRatingRef = useRef(null); // { options, onSelect }
  const begunRef      = useRef(false);
  const skipTargetRef = useRef(0);
  const isFullModeRef = useRef(isFullMode);
  const onStepCompleteRef = useRef(onStepComplete);

  /* keep latest versions of frequently-updated values in refs */
  const flowQuestionsRef  = useRef(flowQuestions);
  const answersRef        = useRef(answers);
  const stepsRef          = useRef(steps);
  const helpPillarsRef    = useRef(helpPillars);
  const answeredCountRef  = useRef(answeredCount);
  const setAnswerFieldRef = useRef(setAnswerField);
  const speakAsyncRef       = useRef(speakAsync);
  const stopSpeakRef          = useRef(stopSpeak);
  const waitForIdleRef        = useRef(waitForIdle);
  const voiceEnabledRef       = useRef(voiceEnabled);

  useEffect(() => { flowQuestionsRef.current   = flowQuestions; }, [flowQuestions]);
  useEffect(() => { answersRef.current        = answers; }, [answers]);
  useEffect(() => { stepsRef.current          = steps; }, [steps]);
  useEffect(() => { helpPillarsRef.current    = helpPillars; }, [helpPillars]);
  useEffect(() => { answeredCountRef.current  = answeredCount; }, [answeredCount]);
  useEffect(() => { setAnswerFieldRef.current = setAnswerField; }, [setAnswerField]);
  useEffect(() => { speakAsyncRef.current       = speakAsync; }, [speakAsync]);
  useEffect(() => { stopSpeakRef.current        = stopSpeak; }, [stopSpeak]);
  useEffect(() => { waitForIdleRef.current      = waitForIdle; }, [waitForIdle]);
  useEffect(() => { voiceEnabledRef.current     = voiceEnabled; }, [voiceEnabled]);
  useEffect(() => { isFullModeRef.current     = isFullMode; }, [isFullMode]);
  useEffect(() => { onStepCompleteRef.current = onStepComplete; }, [onStepComplete]);

  /* cleanup on unmount */
  useEffect(() => () => { cancelRef.current = true; }, []);

  /* ══ Boot help session ═════════════════════════════════════════════ */
  const sessionBootedRef = useRef(false);
  useEffect(() => {
    if (!surveyToken || sessionBootedRef.current) return;
    sessionBootedRef.current = true;
    const ctx = buildHelpContext({
      step: steps?.[0]?.step,
      sectionTitle: steps?.[0]?.title,
      pillars: helpPillars,
      assessmentTrack,
      answeredCount,
      productName: productName(assessmentType),
      typeKey: assessmentType?.type_key || assessmentType?.key,
      isFiveX: isFiveXType(assessmentType),
    });
    createHelpSession(surveyToken, ctx)
      .then((r) => { sessionIdRef.current = r?.data?.session_id || null; })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyToken]);

  /* ══ Core async helpers ════════════════════════════════════════════ */

  function typeSpeedFor(_text, baseMs) {
    if (!voiceEnabledRef.current) return baseMs;
    return typingDelayWithVoice(baseMs);
  }

  /** Speak and type in parallel; typing slows to match voice when enabled. */
  async function speakAndType(text, setter, setCursor, baseMs, runId, { interrupt = false, speakText } = {}) {
    const speech = speakText ?? text;
    const speed = typeSpeedFor(speech, baseMs);
    if (voiceEnabledRef.current) {
      await Promise.all([
        speakAsyncRef.current(speech, { interrupt }),
        typeOut(text, setter, setCursor, speed, runId),
      ]);
      return;
    }
    await typeOut(text, setter, setCursor, baseMs, runId);
  }

  /** Type text character-by-character into a state setter. */
  async function typeOut(text, setter, setCursor, speedMs = 17, runId) {
    if (setCursor) setCursor(true);
    for (let i = 0; i <= text.length; i++) {
      if (cancelRef.current || (runId != null && runIdRef.current !== runId)) break;
      setter(text.slice(0, i));
      if (i < text.length) {
        const ch = text[i];
        const d = [".","!","?"].includes(ch) ? 155 : [",",";",":"].includes(ch) ? 65 : speedMs;
        await sleep(d);
      }
    }
    if (setCursor) setCursor(false);
  }

  /** Fade the screen div out then await the transition. */
  async function fadeOut(runId) {
    await waitForIdleRef.current();
    setVisible(false);
    await sleep(330);
    if (runId != null && runIdRef.current !== runId) return false;
    return true;
  }

  /** Fade the screen div in. */
  async function fadeIn(runId) {
    setVisible(true);
    await sleep(60);
    if (runId != null && runIdRef.current !== runId) return false;
    return true;
  }

  /** Crossfade the AI line — old fades out, new speaks + types in. */
  async function swapAiLine(newText, runId, speed = 20, { interrupt = false } = {}) {
    setAiLineVisible(false);
    await sleep(220);
    if (runId != null && runIdRef.current !== runId) return;
    setAiLine("");
    setAiLineVisible(true);
    await speakAndType(newText, setAiLine, setTypingAiLine, speed, runId, { interrupt });
  }

  /* Skip the welcome sequence and jump straight to questions */
  function handleSkipIntro() {
    stopSpeakRef.current();
    const target = skipTargetRef.current;
    runIdRef.current++; // invalidates the running welcome coroutine
    setPhase("question");
    setVisible(false);
    presentQuestion(target);
  }

  /* ══ Welcome / resume sequence ═════════════════════════════════════ */
  async function runWelcome(lines, targetQuestionIdx = 0) {
    skipTargetRef.current = targetQuestionIdx;
    const runId = ++runIdRef.current;
    const ok = () => !cancelRef.current && runIdRef.current === runId;

    totalWelcomeLines.current = lines.length;
    setPhase("welcome");
    setWelcomeText("");
    setWelcomeIdx(0);

    if (!(await fadeIn(runId))) return;

    for (let i = 0; i < lines.length; i++) {
      if (!ok()) return;
      setWelcomeIdx(i);
      setWelcomeText("");
      setWelcomeTyping(true);

      /* Speak every welcome / resume line */
      await speakAndType(lines[i], setWelcomeText, null, 28, runId);
      setWelcomeTyping(false);
      if (!ok()) return;

      await sleep(i === lines.length - 1 ? 800 : 1100);
      if (!ok()) return;

      /* Fade between messages (not after the last one) */
      if (i < lines.length - 1) {
        setVisible(false);
        await sleep(280);
        if (!ok()) return;
        setVisible(true);
        await sleep(60);
        if (!ok()) return;
      }
    }

    if (!(await fadeOut(runId))) return;
    if (!ok()) return;
    setPhase("question");
    presentQuestion(targetQuestionIdx, runId);
  }

  /* ══ Present one question ══════════════════════════════════════════ */
  async function presentQuestion(index, inheritedRunId) {
    /* Each call gets a fresh run-id; an inherited one is used when
       called synchronously from the same async chain (welcome → question). */
    const runId = inheritedRunId != null ? inheritedRunId : ++runIdRef.current;
    const ok = () => !cancelRef.current && runIdRef.current === runId;

    const qs = flowQuestionsRef.current;
    if (index >= qs.length) {
      /* ── All done ── */
      if (!ok()) return;
      if (!(await fadeOut(runId))) return;
      setPhase("done");
      setConfetti(true);
      setTimeout(() => setConfetti(false), 3200);
      return;
    }

    const question = qs[index];
    const step     = findStepForQuestion(stepsRef.current, question.id);
    const mode     = getQuestionMode(question);
    const impPart  = (question.parts || []).find((p) => p.type === "importance");
    const agrPart  = (question.parts || []).find((p) => p.type === "agreement");
    const textPart = getTextPart(question);
    const dropdownPart = getDropdownPart(question);
    const ratingPart = getRatingPart(question);

    /* Reset locked guards */
    impLockedRef.current = false;
    agrLockedRef.current = false;
    textLockedRef.current = false;
    dropdownLockedRef.current = false;
    starLockedRef.current = false;
    activeRatingRef.current = null;

    /* Store current data for handlers */
    curDataRef.current = { question, impPart, agrPart, textPart, dropdownPart, ratingPart, mode };
    currentIdxRef.current = index;

    const opts_imp = getOptions(impPart, "importance", assessmentType);
    const opts_agr = getOptions(agrPart, "agreement", assessmentType);

    /* Clear display state before fading in */
    setPillarTitle(step?.title || "");
    setPillarIntro(PILLAR_INTROS[step?.title] || "");
    setQuestionNum(index + 1);
    setQuestionId(String(question.id ?? ""));
    setStatementText("");
    setContextTerms([]);
    setAiLine("");
    setAiLineVisible(false);
    setTypingStatement(false);
    setTypingAiLine(false);
    setShowImp(false);
    setImpOptions(opts_imp);
    setImpSelected("");
    setImpLocked(false);
    setShowAgr(false);
    setAgrOptions(opts_agr);
    setAgrSelected("");
    setAgrLocked(false);
    setShowTextInput(false);
    setTextValue("");
    setTextLocked(false);
    setShowDropdown(false);
    setDropdownOptions([]);
    setDropdownSelected("");
    setDropdownLocked(false);
    setShowStars(false);
    setStarSelected(0);
    setStarLocked(false);
    setStarMax(Number(ratingPart?.max_stars) || 5);
    setHelpExchanges([]);

    if (!ok()) return;
    if (!(await fadeIn(runId))) return;
    if (!ok()) return;

    await sleep(280);
    if (!ok()) return;

    /* ─ Type statement — speak it as it appears ─ */
    await speakAndType(`"${question.text}"`, setStatementText, setTypingStatement, 17, runId, {
      speakText: question.text,
    });
    if (!ok()) return;

    /* Terms — inline tooltips after typing completes */
    const terms = (question.terms || []).slice(0, 3);
    if (terms.length) {
      setContextTerms(terms.map((t) => ({ phrase: t.phrase, definition: t.definition })));
    }

    await sleep(240);
    if (!ok()) return;

    /* ─ Text question ─────────────────────────────────────────────── */
    if (mode === "text") {
      const textKey = partAnswerKey(question, textPart);
      const existing = answersRef.current[textKey] ?? textPart.answer ?? "";
      const askText = pick(TEXT_ASKS, "text_ask");
      setAiLineVisible(true);
      await speakAndType(askText, setAiLine, setTypingAiLine, 20, runId);
      if (!ok()) return;
      setTextValue(String(existing || ""));
      setShowTextInput(true);
      return;
    }

    /* ─ Dropdown question ─────────────────────────────────────────── */
    if (mode === "dropdown") {
      const opts = dropdownPart?.options || [];
      const ddKey = partAnswerKey(question, dropdownPart);
      const existing = answersRef.current[ddKey] ?? dropdownPart?.answer ?? "";
      const askText = pick(DROPDOWN_ASKS, "dd_ask");
      setAiLineVisible(true);
      await speakAndType(askText, setAiLine, setTypingAiLine, 20, runId);
      if (!ok()) return;
      setDropdownOptions(opts);
      if (existing) setDropdownSelected(String(existing));
      setShowDropdown(true);
      activeRatingRef.current = { options: opts, onSelect: handleDropdownSelect };
      return;
    }

    /* ─ Star rating question ──────────────────────────────────────── */
    if (mode === "rating") {
      const max = Number(ratingPart?.max_stars) || 5;
      const starKey = partAnswerKey(question, ratingPart);
      const existing = answersRef.current[starKey] ?? ratingPart?.answer ?? "";
      const askText = `On a scale of 1 to ${max}, how would you rate this?`;
      setAiLineVisible(true);
      await speakAndType(askText, setAiLine, setTypingAiLine, 20, runId);
      if (!ok()) return;
      setStarMax(max);
      if (existing) setStarSelected(Number(existing) || 0);
      setShowStars(true);
      return;
    }

    /* ─ Standard MCQ: importance then agreement ─────────────────── */
    const impAskText = pick(IMPORTANCE_ASKS, "imp_ask");
    setAiLineVisible(true);
    await speakAndType(impAskText, setAiLine, setTypingAiLine, 20, runId);
    if (!ok()) return;

    await sleep(80);
    setShowImp(true);
    activeRatingRef.current = { options: opts_imp, onSelect: handleImportanceSelect };
  }

  /** Shared post-answer flow: ack/observation → pillar done → transition → next Q */
  async function advanceAfterAnswer(runId, lineText) {
    const ok = () => !cancelRef.current && runIdRef.current === runId;

    if (lineText) {
      await swapAiLine(lineText, runId);
      if (!ok()) return;
      await sleep(350);
      if (!ok()) return;
    }

    const d2 = curDataRef.current;
    const currentStepData = findStepForQuestion(stepsRef.current, d2?.question?.id);
    const updatedAnswers = answersRef.current;
    const isLastInScope = currentIdxRef.current + 1 >= flowQuestionsRef.current.length;
    let pillarJustDone = false;
    if (currentStepData) {
      const pillarQs = isFullModeRef.current
        ? flowQuestionsRef.current
        : (currentStepData.questions || []);
      pillarJustDone = pillarQs.every((q) => {
        if (String(q.id) === String(d2?.question?.id)) return true;
        return isQuestionAnswered(q, updatedAnswers);
      });
    }

    if (pillarJustDone) {
      const pillarTitleDone = currentStepData?.title || "This pillar";
      const PILLAR_DONE_MSGS = [
        `${pillarTitleDone} — all done. Great focus.`,
        `That wraps up ${pillarTitleDone}. On to the next section.`,
        `${pillarTitleDone} complete. Strong work.`,
        `All ${pillarTitleDone} questions answered. Moving forward.`,
        `Solid. ${pillarTitleDone} is fully covered.`,
      ];
      const doneMsg = PILLAR_DONE_MSGS[Math.floor(Math.random() * PILLAR_DONE_MSGS.length)];
      setAiLineVisible(false);
      await sleep(160);
      if (!ok()) return;
      setAiLine("");
      setAiLineVisible(true);
      await speakAndType(doneMsg, setAiLine, null, 20, runId);
      if (!ok()) return;
      await sleep(400);
      if (!ok()) return;
    }

    const trans = pick(TRANSITIONS, "trans");
    setAiLineVisible(false);
    await sleep(160);
    if (!ok()) return;
    setAiLine("");
    setAiLineVisible(true);
    await speakAndType(trans, setAiLine, null, 20, runId);
    if (!ok()) return;
    await sleep(350);
    if (!ok()) return;

    if (!(await fadeOut(runId))) return;
    if (!ok()) return;

    if (isFullModeRef.current && isLastInScope) {
      try {
        const result = await onStepCompleteRef.current?.();
        if (result?.finished) {
          setPhase("done");
          setConfetti(true);
          setTimeout(() => setConfetti(false), 3200);
        }
      } catch {
        /* Parent shows validation/save errors */
      }
      return;
    }

    presentQuestion(currentIdxRef.current + 1, runId);
  }

  async function handleTextSubmit() {
    if (textLockedRef.current) return;
    const val = textValue.trim();
    if (!val || (val.length < 2 && val.toUpperCase() !== "NA")) return;

    textLockedRef.current = true;
    stopSpeakRef.current();
    setTextLocked(true);
    setShowTextInput(false);

    const d = curDataRef.current;
    const part = d?.textPart || getTextPart(d?.question);
    setAnswerFieldRef.current(d.question, part, val);

    const runId = runIdRef.current;
    await advanceAfterAnswer(runId, pick(FREE_ACKS, "free_ack"));
  }

  async function handleDropdownSelect(value) {
    if (dropdownLockedRef.current) return;
    dropdownLockedRef.current = true;
    activeRatingRef.current = null;
    stopSpeakRef.current();

    setDropdownSelected(value);
    setDropdownLocked(true);
    setShowDropdown(false);

    const d = curDataRef.current;
    const part = d?.dropdownPart || getDropdownPart(d?.question);
    setAnswerFieldRef.current(d.question, part, value);

    const sid = sessionIdRef.current;
    if (sid) {
      updateHelpSessionContext(surveyToken, sid, {
        focused_question_id: String(d.question.id),
        current_agreement_answer: value,
      }).catch(() => {});
    }

    const runId = runIdRef.current;
    await advanceAfterAnswer(runId, pick(FREE_ACKS, "free_ack"));
  }

  async function handleStarSelect(n) {
    if (starLockedRef.current) return;
    starLockedRef.current = true;
    stopSpeakRef.current();

    setStarSelected(n);
    setStarLocked(true);
    setShowStars(false);

    const d = curDataRef.current;
    const part = d?.ratingPart || getRatingPart(d?.question);
    setAnswerFieldRef.current(d.question, { ...part, type: "rating" }, String(n));

    const runId = runIdRef.current;
    await advanceAfterAnswer(runId, pick(FREE_ACKS, "free_ack"));
  }

  /* ══ Importance selected ═══════════════════════════════════════════ */
  async function handleImportanceSelect(value) {
    if (impLockedRef.current) return;
    impLockedRef.current = true;
    activeRatingRef.current = null;

    /* User picked importance — interrupt the current prompt and respond. */
    stopSpeakRef.current();

    setImpSelected(value);
    setImpLocked(true);
    impValueRef.current = value;

    const runId = runIdRef.current; // same ongoing sequence
    const ok = () => !cancelRef.current && runIdRef.current === runId;

    const iLevel = { "Not Important": 0, "Important": 1, "Very Important": 2, "Critical": 3 }[value] ?? 0;
    const ack = iLevel >= 2 ? pick(IMP_ACKS_HIGH, "ack_h")
              : iLevel === 1 ? pick(IMP_ACKS_MED,  "ack_m")
              : pick(IMP_ACKS_LOW, "ack_l");
    const agrAsk = iLevel >= 2 ? pick(AGR_ASKS_CRITICAL,  "agr_h")
                 : iLevel === 1 ? pick(AGR_ASKS_IMPORTANT, "agr_m")
                 : pick(AGR_ASKS_LOW, "agr_l");
    const combined = ack ? `${ack} ${agrAsk}` : agrAsk;

    await swapAiLine(combined, runId, 20, { interrupt: true });
    if (!ok()) return;

    /* Fade out the importance row, then reveal agreement */
    setShowImp(false);
    await sleep(200);
    setShowAgr(true);

    const opts_agr = getOptions(curDataRef.current?.agrPart, "agreement", assessmentType);
    activeRatingRef.current = { options: opts_agr, onSelect: handleAgreementSelect };
  }

  /* ══ Agreement selected ════════════════════════════════════════════ */
  async function handleAgreementSelect(value) {
    if (agrLockedRef.current) return;
    agrLockedRef.current = true;
    activeRatingRef.current = null;
    stopSpeakRef.current();

    setAgrSelected(value);
    setAgrLocked(true);

    const d = curDataRef.current;
    if (d?.impPart) setAnswerFieldRef.current(d.question, d.impPart, impValueRef.current);
    if (d?.agrPart) setAnswerFieldRef.current(d.question, d.agrPart, value);

    const sid = sessionIdRef.current;
    if (sid) {
      updateHelpSessionContext(surveyToken, sid, {
        focused_question_id: String(d.question.id),
        current_importance_answer: impValueRef.current,
        current_agreement_answer: value,
      }).catch(() => {});
    }

    const runId = runIdRef.current;
    const obs = pick(getObsArray(impValueRef.current, value), "obs");
    await advanceAfterAnswer(runId, obs);
  }

  /* ══ Begin / resume entry-points ═══════════════════════════════════ */
  function handleBegin() {
    if (begunRef.current) return;
    begunRef.current = true;

    updateAssessmentStatus(surveyToken, "Pending").catch(() => {});

    const qs  = flowQuestionsRef.current;
    const ans = answersRef.current;
    const idx = qs.findIndex((q) => !isQuestionAnswered(q, ans));
    const target = idx >= 0 ? idx : 0;

    if (isFullModeRef.current) {
      const stepTitle = stepsRef.current?.[currentStep - 1]?.title;
      const total = totalSteps || stepsRef.current?.length || 6;
      if (answeredCountRef.current > 0) {
        runWelcome(makeFullResumeLines(answeredCountRef.current), target);
      } else if (skipLanding) {
        runWelcome(makeFullPillarIntroLines(stepTitle, currentStep, total), target);
      } else {
        runWelcome(
          buildWelcomeLines({
            questionCount: introQuestionCountRef.current || flowQuestionsRef.current.length,
            pillarCount: (stepsRef.current || []).length,
            isFull: true,
            assessmentType,
          }),
          target
        );
      }
      return;
    }

    if (answeredCountRef.current > 0) {
      runWelcome(
        makeResumeLines(
          answeredCountRef.current,
          flowQuestionsRef.current.length
        ),
        target
      );
    } else {
      const pillarCount = (stepsRef.current || []).filter((s) =>
        (s.questions || []).some((q) =>
          flowQuestionsRef.current.some((fq) => fq.id === q.id)
        )
      ).length;
      runWelcome(
        buildWelcomeLines({
          questionCount: flowQuestionsRef.current.length,
          pillarCount,
          isFull: false,
          assessmentType,
        }),
        target
      );
    }
  }

  /* Auto-start when resuming (lite) or entering a later pillar (full) */
  const autoStartRef = useRef(false);
  useEffect(() => {
    if (autoStartRef.current || !flowQuestions.length) return;

    if (skipLanding && isFullMode) {
      autoStartRef.current = true;
      begunRef.current = true;
      const idx = flowQuestions.findIndex((q) => !isQuestionAnswered(q, answers));
      const stepTitle = steps?.[currentStep - 1]?.title;
      const total = totalSteps || steps?.length || 6;
      const lines = scopeAnsweredCount > 0
        ? makeFullResumeLines(answeredCount)
        : makeFullPillarIntroLines(stepTitle, currentStep, total);
      runWelcome(lines, idx >= 0 ? idx : 0);
      return;
    }

    if (skipLanding || answeredCount === 0) return;
    autoStartRef.current = true;
    begunRef.current = true;
    const idx = flowQuestions.findIndex((q) => !isQuestionAnswered(q, answers));
    runWelcome(
      isFullMode
        ? makeFullResumeLines(answeredCount)
        : makeResumeLines(answeredCount, flowQuestions.length),
      idx >= 0 ? idx : 0
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowQuestions.length, skipLanding, isFullMode]);

  /* ══ Help question from input bar ══════════════════════════════════ */
  async function handleUserQuestion(text, displayQ = null) {
    if (!text.trim()) return;

    /* Add a loading exchange immediately so the user sees their message */
    const idx = helpExchanges.length;
    const shownQ = displayQ || (
      /^Explain question\s+/i.test(text.trim())
        ? "Explain more"
        : /^Explain this statement/i.test(text.trim())
          ? "Explain more"
          : text
    );
    setHelpExchanges((prev) => [...prev, { q: shownQ, a: "", loading: true }]);
    setAiLoading(true);

    /* Scroll to bottom after adding the user bubble */
    setTimeout(() => convoEndRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

    try {
      let sid = sessionIdRef.current;
      if (!sid) {
        const s = findStepForQuestion(stepsRef.current, curDataRef.current?.question?.id);
        const ctx = buildHelpContext({
          step: s?.step, sectionTitle: s?.title,
          pillars: helpPillarsRef.current, assessmentTrack,
          answeredCount: answeredCountRef.current,
          productName: productName(assessmentType),
          typeKey: assessmentType?.type_key || assessmentType?.key,
          isFiveX: isFiveXType(assessmentType),
        });
        const r = await createHelpSession(surveyToken, ctx);
        sid = r?.data?.session_id || null;
        sessionIdRef.current = sid;
      }
      if (!sid) throw new Error("no session");

      const s = findStepForQuestion(stepsRef.current, curDataRef.current?.question?.id);
      const ctx = buildHelpContext({
        step: s?.step, sectionTitle: s?.title,
        pillars: helpPillarsRef.current, assessmentTrack,
        focusedPillarStep: s?.step,
        focusedQuestionId: String(curDataRef.current?.question?.id || ""),
        answeredCount: answeredCountRef.current,
        productName: productName(assessmentType),
        typeKey: assessmentType?.type_key || assessmentType?.key,
        isFiveX: isFiveXType(assessmentType),
      });
      const r = await sendHelpMessage(surveyToken, sid, text, ctx);
      /* Strip any "On question X.XX, " prefix the backend occasionally prepends */
      const rawReply = r?.data?.reply || "I'm not sure about that — try asking about a specific term.";
      const reply = rawReply.replace(/^On question\s+[\d.]+[,.]?\s*/i, "");

      /* Switch from loading spinner to typewriter */
      setHelpExchanges((prev) =>
        prev.map((ex, i) => i === idx ? { ...ex, loading: false, a: "", typing: true } : ex)
      );
      setAiLoading(false);

      if (voiceEnabledRef.current) {
        await speakAndType(reply, (partial) => {
          setHelpExchanges((prev) =>
            prev.map((ex, i) => i === idx ? { ...ex, a: partial } : ex)
          );
        }, null, 14, runIdRef.current);
      } else {
        const helpCharDelay = typeSpeedFor(reply, 14);
        for (let j = 1; j <= reply.length; j++) {
          const ch = reply[j - 1];
          setHelpExchanges((prev) =>
            prev.map((ex, i) => i === idx ? { ...ex, a: reply.slice(0, j) } : ex)
          );
          setTimeout(() => convoEndRef.current?.scrollIntoView({ behavior: "smooth" }), 0);
          const delay = [".", "!", "?"].includes(ch) ? helpCharDelay * 3.2 : [",", ";", ":"].includes(ch) ? helpCharDelay * 1.6 : helpCharDelay;
          await sleep(delay);
        }
      }

      setHelpExchanges((prev) =>
        prev.map((ex, i) => i === idx ? { ...ex, typing: false } : ex)
      );
    } catch {
      setHelpExchanges((prev) =>
        prev.map((ex, i) => i === idx ? { ...ex, a: "Couldn't reach the guide right now. Please try again.", loading: false, typing: false } : ex)
      );
      setAiLoading(false);
    } finally {
      setTimeout(() => convoEndRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    }
  }

  /* ══ Mic handler ═══════════════════════════════════════════════════ */
  function handleMicClick() {
    if (micListening) { stopSTT(); return; }
    startSTT((transcript) => {
      const rating = activeRatingRef.current;
      if (rating) {
        const match = matchVoiceToOption(transcript, rating.options);
        if (match) { rating.onSelect(match); return; }
      }
      if (transcript) handleUserQuestion(transcript);
    });
  }

  /* ══ Theme tokens ══════════════════════════════════════════════════ */
  const T = assessmentChromeTokens(dark, primaryColor);

  /* ══ Render ════════════════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        @keyframes lga-shimmer   { 0%{background-position:-200% center}100%{background-position:200% center} }
        @keyframes lga-cursor    { 0%,100%{opacity:1}50%{opacity:0} }
        @keyframes lga-bounce    { 0%,80%,100%{transform:translateY(0);opacity:.45}40%{transform:translateY(-5px);opacity:1} }
        @keyframes lga-fadein    { from{opacity:0}to{opacity:1} }
        @keyframes lga-ratingin  { from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)} }
        @keyframes lga-confetti  { 0%{opacity:1;transform:translate(0,0) rotate(0deg) scale(1)}80%{opacity:.7}100%{opacity:0;transform:translate(var(--x),var(--y)) rotate(var(--r)) scale(0.4)} }
        @keyframes lga-orb-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(0,79,115,0.4)}50%{box-shadow:0 0 0 18px rgba(0,79,115,0)} }
        @keyframes lga-pulse-dot { 0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.75)} }
        @keyframes lga-progress  { 0%{background-position:-200% center}100%{background-position:200% center} }
      `}</style>

      <Confetti active={confetti} />

      <div className="lga-app" style={{
        display: "flex", flexDirection: "column",
        background: T.page, position: "relative", overflow: "hidden",
      }}>
        {/* dot-grid texture */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: T.pageDots,
          backgroundSize: "28px 28px",
        }} />

        <AssessmentSharedNav
          T={T}
          companyName={companyName}
          productName={
            isFiveXType(assessmentType)
              ? null
              : productDisplayName || productName(assessmentType)
          }
          productLogoUrl={productLogoUrl}
          modeBadge="Conversational"
          uxMode={uxMode}
          onUxModeChange={onUxModeChange}
          showProgress={phase === "question"}
          progressPct={progressPct}
          questionsLeft={progressQuestions.length - answeredCount}
          ttsSupported={ttsSupported}
          voiceEnabled={voiceEnabled}
          onToggleVoice={() => setVoiceEnabled((v) => !v)}
          onToggleTheme={toggleTheme}
          useDefaultProductLogo={isFiveXType(assessmentType)}
        />

        {phase === "question" ? <AssessmentRatingGuide T={T} assessmentType={assessmentType} /> : null}

        {phase === "question" ? (
          <AssessmentPillarStatus
            T={T}
            steps={steps}
            answers={answers}
            activeStep={
              findStepForQuestion(
                steps,
                questionId || flowQuestions[currentIdxRef.current]?.id
              )?.step ?? (isFullMode ? currentStep : steps?.[0]?.step)
            }
          />
        ) : null}

        {phase === "question" ? (() => {
          const activeQid =
            questionId || flowQuestions[currentIdxRef.current]?.id;
          const activeStepData =
            findStepForQuestion(steps, activeQid) ||
            (isFullMode ? steps?.[Number(currentStep) - 1] : null);
          const pillarNavQs = (activeStepData?.questions || []).filter((q) =>
            flowQuestions.some((fq) => String(fq.id) === String(q.id))
          );
          return (
            <AssessmentQuestionNav
              T={T}
              questions={
                pillarNavQs.length
                  ? pillarNavQs
                  : activeStepData?.questions || flowQuestions
              }
              answers={answers}
              activeQuestionId={activeQid}
            />
          );
        })() : null}

        {/* ── Content area ─────────────────────────────────────── */}
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden", position: "relative", zIndex: 10, display: "flex", flexDirection: "column" }}>

          {/* Landing */}
          {phase === "landing" && (
            <LandingScreen
              companyName={companyName}
              companyLogo={companyLogo}
              onBegin={handleBegin}
              dark={dark}
              assessmentTrack={assessmentTrack}
              trackBanner={trackBanner}
              questionCount={introQuestionCount}
              assessmentType={assessmentType}
            />
          )}

          {/* Welcome / resume intro */}
          {phase === "welcome" && (
            <div style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              padding: "32px 24px", textAlign: "center",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(6px)",
              transition: "opacity 0.3s ease, transform 0.3s ease",
            }}>
              <AiDot size={56} dark={dark} pulse />
              <div style={{ height: 28 }} />
              <p style={{
                fontSize: welcomeIdx === 0 ? 20 : 17,
                fontWeight: welcomeIdx === 0 ? 700 : 400,
                lineHeight: 1.75, maxWidth: 480,
                color: welcomeIdx === 0 ? (dark ? "#fff" : "#0f172a") : (dark ? "rgba(255,255,255,0.68)" : "#475569"),
                letterSpacing: welcomeIdx === 0 ? "-0.01em" : "normal",
                minHeight: "2.5em",
              }}>
                {welcomeText}{welcomeTyping && <Cursor />}
              </p>
              {/* Progress dots */}
              <div style={{ display: "flex", gap: 6, marginTop: 20 }}>
                {Array.from({ length: totalWelcomeLines.current }).map((_, i) => (
                  <div key={i} style={{
                    width: i === welcomeIdx ? 20 : 6, height: 6, borderRadius: 3,
                    background: i === welcomeIdx ? T.brand : dark ? "rgba(255,255,255,0.18)" : "rgba(0,79,115,0.18)",
                    transition: "all 0.3s ease",
                  }} />
                ))}
              </div>
              {/* Skip intro — below the dots */}
              <button
                type="button" onClick={handleSkipIntro}
                style={{
                  marginTop: 28, display: "inline-flex", alignItems: "center", gap: 4,
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                  background: "none", border: "none",
                  color: dark ? "rgba(255,255,255,0.30)" : "rgba(0,79,115,0.35)",
                  padding: "4px 8px",
                }}
              >
                Skip intro
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          )}

          {/* Question stage — inline conversational layout */}
          {phase === "question" && (
            <div className="lga-question-stage" style={{
              flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(10px)",
              transition: "opacity 0.32s ease, transform 0.32s ease",
            }}>
              <div style={{ width: "100%", maxWidth: 960 }}>

                {/* ── Pillar tag + question counter ─────────────────── */}
                <div className="lga-pillar-header" style={{ marginBottom: 18 }}>
                  <div>
                    <span style={{
                      fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase",
                      color: T.pillarColor, padding: "3px 12px", borderRadius: 999,
                      background: T.pillarBg, border: `1px solid ${T.pillarBorder}`,
                    }}>
                      {pillarTitle}
                    </span>
                    {pillarIntro && (
                      <p style={{ fontSize: 12, color: T.text3, marginTop: 5, lineHeight: 1.55, maxWidth: 560 }}>
                        {pillarIntro}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, paddingTop: 2 }}>
                    {questionId && (
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: T.brand, fontFamily: "monospace" }}>
                        {questionId}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: T.text3 }}>
                      {questionNum} / {flowQuestions.length}
                    </span>
                  </div>
                </div>

                {/* ── AI: statement (top, prominent card) ──────────── */}
                <div className="lga-statement-card" style={{
                  display: "flex", flexDirection: "column", gap: 10,
                  background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 18,
                  padding: "20px 22px", marginBottom: 18, minHeight: 80,
                  boxShadow: dark
                    ? "0 4px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)"
                    : "0 4px 20px rgba(0,79,115,0.07)",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <AiDot size={30} dark={dark} />
                    <p style={{
                      fontSize: 17, fontWeight: 500, lineHeight: 1.78,
                      color: T.text1, margin: 0, flex: 1, letterSpacing: "-0.005em",
                    }}>
                      {typingStatement
                        ? <>{statementText}<Cursor /></>
                        : <RichStatement text={statementText} terms={contextTerms} dark={dark} />
                      }
                    </p>
                  </div>
                  {!typingStatement && statementText && (
                    <button
                      type="button"
                      onClick={() => {
                        const q = curDataRef.current?.question;
                        const qid = q?.id != null ? String(q.id) : "";
                        const qtext = String(q?.text || statementText || "").trim();
                        if (!qid && !qtext) return;
                        const prompt = qid
                          ? `Explain question ${qid}: ${qtext}`
                          : `Explain this statement: ${qtext}`;
                        handleUserQuestion(prompt, "Explain more");
                      }}
                      disabled={aiLoading}
                      aria-label="Explain this question in more detail"
                      style={{
                        alignSelf: "flex-end",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 12px",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "inherit",
                        cursor: aiLoading ? "not-allowed" : "pointer",
                        opacity: aiLoading ? 0.55 : 1,
                        color: dark ? "#7dd3fc" : "#004f73",
                        background: dark ? "rgba(56,189,248,0.12)" : "rgba(0,79,115,0.08)",
                        border: dark ? "1px solid rgba(56,189,248,0.25)" : "1px solid rgba(0,79,115,0.16)",
                        transition: "background 0.15s ease, border-color 0.15s ease",
                      }}
                    >
                      <span aria-hidden style={{ fontSize: 13 }}>✦</span>
                      Explain more
                    </button>
                  )}
                </div>

                {/* ── Inline help conversation ──────────────────────── */}
                {helpExchanges.map((ex, i) => (
                  <div key={i} style={{ marginBottom: 4, animation: "lga-fadein 0.25s ease both" }}>
                    {/* User bubble — right-aligned */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                      <div style={{
                        maxWidth: "72%", padding: "10px 15px",
                        borderRadius: "18px 18px 4px 18px",
                        background: dark ? "rgba(0,79,115,0.30)" : "rgba(0,79,115,0.09)",
                        border: dark ? "1px solid rgba(56,189,248,0.22)" : "1px solid rgba(0,79,115,0.16)",
                        fontSize: 14, color: T.text1, lineHeight: 1.6, fontWeight: 500,
                      }}>
                        {ex.q}
                      </div>
                    </div>
                    {/* AI answer — left-aligned, same style as AI line */}
                    {(ex.a || ex.loading || ex.typing) && (
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                        <AiDot size={26} dark={dark} />
                        <div style={{ flex: 1, paddingTop: 3 }}>
                          {ex.loading
                            ? <ThreeDots dark={dark} />
                            : <p style={{ fontSize: 14, color: T.text2, lineHeight: 1.68, margin: 0 }}>
                                <MdText>{ex.a}</MdText>
                                {ex.typing && <Cursor />}
                              </p>
                          }
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* ── AI line: importance ask / ack+agreement ask / observation / transition ── */}
                {(aiLine || typingAiLine) && (
                  <div style={{
                    display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16,
                    opacity: aiLineVisible ? 1 : 0,
                    transition: "opacity 0.22s ease",
                  }}>
                    <AiDot size={26} dark={dark} />
                    <p style={{
                      fontSize: 14, color: T.text2, lineHeight: 1.65,
                      margin: 0, flex: 1, paddingTop: 4,
                    }}>
                      {aiLine}{typingAiLine && <Cursor />}
                    </p>
                  </div>
                )}

                {/* ── Rating buttons — always at the bottom of the conversation ── */}
                {showStars && (
                  <div className="lga-rating-indent" style={{ marginBottom: 22, animation: "lga-ratingin 0.3s ease both" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text3, marginBottom: 9 }}>
                      Rate 1–{starMax}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }} role="radiogroup" aria-label="Star rating">
                      {Array.from({ length: starMax }, (_, i) => i + 1).map((n) => {
                        const on = starSelected >= n;
                        return (
                          <button
                            key={n}
                            type="button"
                            disabled={starLocked}
                            aria-checked={starSelected === n}
                            role="radio"
                            onClick={() => handleStarSelect(n)}
                            title={`${n} star${n === 1 ? "" : "s"}`}
                            style={{
                              fontSize: 28, lineHeight: 1, border: "none", background: "transparent",
                              cursor: starLocked ? "default" : "pointer",
                              color: on ? "#fbbf24" : (dark ? "rgba(255,255,255,0.25)" : "#cbd5e1"),
                              padding: 2,
                            }}
                          >
                            ★
                          </button>
                        );
                      })}
                      {starSelected > 0 && (
                        <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 600, color: T.text2 }}>
                          {starSelected}/{starMax}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {showImp && (
                  <div className="lga-rating-indent" style={{ marginBottom: 22, animation: "lga-ratingin 0.3s ease both" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text3, marginBottom: 9 }}>
                      Importance
                    </div>
                    <RatingRow options={impOptions} selected={impSelected} locked={impLocked} onSelect={handleImportanceSelect} dark={dark} />
                  </div>
                )}
                {showAgr && (
                  <div className="lga-rating-indent" style={{ marginBottom: 22, animation: "lga-ratingin 0.3s ease both" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text3, marginBottom: 9 }}>
                      Agreement
                    </div>
                    <RatingRow options={agrOptions} selected={agrSelected} locked={agrLocked} onSelect={handleAgreementSelect} dark={dark} compact />
                  </div>
                )}

                {showTextInput && (
                  <div className="lga-rating-indent" style={{ marginBottom: 22, animation: "lga-ratingin 0.3s ease both" }}>
                    <textarea
                      value={textValue}
                      onChange={(e) => !textLocked && setTextValue(e.target.value)}
                      disabled={textLocked}
                      rows={4}
                      placeholder="Enter your response (at least 2 characters, or NA)..."
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleTextSubmit();
                        }
                      }}
                      style={{
                        width: "100%", boxSizing: "border-box",
                        padding: "12px 14px", borderRadius: 10, resize: "vertical",
                        fontSize: 14, lineHeight: 1.55, fontFamily: "inherit",
                        background: dark ? "rgba(255,255,255,0.05)" : "#fff",
                        border: dark ? "1.5px solid rgba(255,255,255,0.12)" : "1.5px solid rgba(0,79,115,0.18)",
                        color: T.text1,
                        outline: "none",
                      }}
                    />
                    <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        disabled={textLocked || !(textValue.trim().length >= 2 || textValue.trim().toUpperCase() === "NA")}
                        onClick={handleTextSubmit}
                        style={{
                          padding: "9px 22px", borderRadius: 999, border: "none",
                          fontSize: 13, fontWeight: 700, cursor: textLocked ? "default" : "pointer",
                          opacity: textLocked || !(textValue.trim().length >= 2 || textValue.trim().toUpperCase() === "NA") ? 0.45 : 1,
                          background: "#004f73", color: "#fff",
                          boxShadow: dark ? "0 0 0 3px rgba(56,189,248,0.12)" : "0 0 0 3px rgba(0,79,115,0.10)",
                        }}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}

                {showDropdown && (
                  <div className="lga-rating-indent" style={{ marginBottom: 22, animation: "lga-ratingin 0.3s ease both" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text3, marginBottom: 9 }}>
                      Select one
                    </div>
                    <RatingRow
                      options={dropdownOptions}
                      selected={dropdownSelected}
                      locked={dropdownLocked}
                      onSelect={handleDropdownSelect}
                      dark={dark}
                      compact
                    />
                  </div>
                )}

                {/* Scroll anchor */}
                <div ref={convoEndRef} />
              </div>
            </div>
          )}

          {/* Done */}
          {phase === "done" && (
            <DoneScreen onComplete={onComplete} saving={saving} dark={dark} assessmentTrack={assessmentTrack} />
          )}
        </div>

        {/* ── Input bar — inline at bottom of column (no fixed) ────── */}
        {phase === "question" && (
          <ChatInputBar
            inline
            onSend={handleUserQuestion}
            onComplete={onComplete}
            showComplete={isFullMode ? allAssessmentAnswered : allScopeAnswered}
            saving={saving}
            aiLoading={aiLoading}
            unansweredCount={progressQuestions.length - answeredCount}
            firstUnansweredId={null}
            micSupported={sttSupported}
            micListening={micListening}
            onMicClick={handleMicClick}
          />
        )}
      </div>
    </>
  );
}
